import { createFeatureSelector, createSelector } from '@ngrx/store';

import { MastersMetaData } from '../../models/masters/mastersmetadata.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { MASTERS_REDUCER_KEY } from '../reducers';
import { MastersInitialState } from '../states/master-management.state';
import { getNotificationsMap$ } from './notifications.selector';
import { getCurrentOrganizationId$ } from './session.selector';

const currentState = createFeatureSelector<MastersInitialState>(MASTERS_REDUCER_KEY);

export const getMastersOrganizationMap$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state.organizationsMap?.[organizationId]
);

export const getMasters$ = createSelector(getMastersOrganizationMap$, organizationMap => organizationMap?.masters || []);

export const getSelectedTableId$ = createSelector(currentState, state => state.selectedTableId || '');

export const getSelectedRestApiName$ = createSelector(currentState, state => state.selectedRestApiName || '');

export const getParentTableId$ = createSelector(currentState, state => state.parentTableId);

export const getParentTableRestApiName$ = createSelector(currentState, state => state.parentTableRestApiName || '');

export const getMastersMetaDataMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.metaData);

const getMastersLanguageTemplateMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.languageTemplate);

const getMastersSearchFiltersMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.searchFilters);

const getMastersPaginationMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.pagination);

const getMastersRequestsMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.requests);

const getMastersRequestsPaginationMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.requestsPagination);

export const getMastersRecordsMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.records);

const getMastersParentPrimaryKeysMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.parentPrimaryKeys);

const getMastersInboundMappingsMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.inboundMappings);

const getMastersOutboundMappingsMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.outboundMappings);

const getMastersReportDetailsMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.reportDetails);

const getMastersUploadedFilesMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.uploadedFiles);

const getMastersChainDisplayNamesMap$ = createSelector(getMastersOrganizationMap$, masterOrgMap => masterOrgMap?.chainDisplayNames);

export const getParentPrimaryKeysMap$ = createSelector(getMastersParentPrimaryKeysMap$, parentPrimaryKeysMap => parentPrimaryKeysMap);

export const getMasterLanguageTemplate$ = createSelector(
    getMastersLanguageTemplateMap$,
    getSelectedRestApiName$,
    (languageTemplateMap, restApiName) => languageTemplateMap?.[restApiName]
);

const getSelectedMasterMetadata$ = createSelector(
    getMastersMetaDataMap$,
    getSelectedTableId$,
    (masterMetaDataMap, tableId) => masterMetaDataMap?.[tableId]
);

const getPrimaryKeys = (tableId: string, keys: string[], masterMetaDataMap: { [property: string]: MastersMetaData }) => {
    if (!tableId || !masterMetaDataMap || !masterMetaDataMap?.[tableId]) {
        return keys;
    }
    if (masterMetaDataMap?.[tableId]?.masterMetadata?.parentTableId?.length > 0) {
        getPrimaryKeys(masterMetaDataMap?.[tableId]?.masterMetadata?.parentTableId, keys, masterMetaDataMap);
    }
    masterMetaDataMap?.[tableId]?.masterMetadata?.keyColumns?.forEach(key => {
        keys.push(key);
    });
    return keys;
};

export const getSelectedMasterKeys$ = createSelector(
    getSelectedMasterMetadata$,
    getMastersMetaDataMap$,
    (masterMetaData, masterMetaDataMap) => {
        const fieldIds: string[] = [];
        getPrimaryKeys(masterMetaData?.masterItem?.itemId, fieldIds, masterMetaDataMap);
        return fieldIds;
    }
);

export const getMasterMetadata$ = createSelector(getSelectedMasterMetadata$, getMasterLanguageTemplate$, (data, template) => {
    const metaData: MastersMetaData = CommonUtilsService.cloneObject(data);
    if (metaData?.masterItem?.itemId?.length > 0 && template !== undefined) {
        metaData['languageData'] = template;
        return new MastersMetaData(metaData);
    }
    return undefined;
});

export const getSearchFilters$ = createSelector(
    getMastersSearchFiltersMap$,
    getSelectedRestApiName$,
    (searchFiltersMap, restApiName) => searchFiltersMap?.[restApiName]
);

export const getMasterRecordPagination$ = createSelector(
    getMastersPaginationMap$,
    getSelectedRestApiName$,
    (paginationMap, restApiName) => paginationMap?.[restApiName]
);

export const getSearchedRecordsCount$ = createSelector(currentState, state => state.masterSearchCount);

export const getMasterRecordsMap$ = createSelector(getMastersRecordsMap$, getSelectedRestApiName$, (records, restApiName) => {
    return records?.[restApiName];
});

export const getMasterRequestsMap$ = createSelector(
    getMastersRequestsMap$,
    getSelectedRestApiName$,
    (requestsMap, restAPIName) => requestsMap?.[restAPIName]
);

export const getParentMasterRecords$ = createSelector(
    getMastersRecordsMap$,
    getParentTableRestApiName$,
    (recordsMap, restAPIName) => recordsMap?.[restAPIName]
);

export const getMasterRecords$ = createSelector(getMasterRecordsMap$, getMasterRecordPagination$, (records, pagination) => {
    const start = pagination?.pageIndex * pagination?.pageSize;
    const end = (pagination?.pageIndex + 1) * pagination?.pageSize;
    return records?.filter((_record, index) => index >= start && index < end) || [];
});

export const getMasterRecordsPagingState$ = createSelector(
    getMasterRecordPagination$,
    getSearchedRecordsCount$,
    getMasterRecordsMap$,
    (pagination, recordsCount, records) => {
        const start = pagination?.pageIndex * pagination?.pageSize;
        const end = (pagination?.pageIndex + 1) * pagination?.pageSize;
        const pagingState =
            pagination?.pageState?.[pagination?.pageIndex]?.trim().length > 0 && pagination?.pageState?.[pagination?.pageIndex];
        const searchAfter = pagination?.search?.searchAfter?.length > 0 && (pagination?.search?.searchAfter as any);
        return {
            pagingState: pagingState || searchAfter,
            count: recordsCount || records?.length,
            start: start,
            end: end,
        };
    }
);

export const getIsRecordsLoading$ = createSelector(currentState, state => state.isRecordsLoading);

export const getParentPrimaryKeys$ = createSelector(
    getParentPrimaryKeysMap$,
    getParentTableRestApiName$,
    getParentTableId$,
    (keysMap, restApiName, tableId) => {
        const keyColumns = keysMap?.[tableId] || [];
        if (keyColumns.length > 0 && restApiName.length > 0) {
            return {
                keyColumns,
                restApiName,
            };
        }
        return undefined;
    }
);

export const getMastersInboundMappings$ = createSelector(
    getMastersInboundMappingsMap$,
    getSelectedTableId$,
    (inboundMappingsMap, tableId) => inboundMappingsMap?.[tableId] || undefined
);

export const getMasterchainDisplayNames$ = createSelector(
    getMastersChainDisplayNamesMap$,
    getSelectedRestApiName$,
    (chainDisplayNamesMap, restApiName) => chainDisplayNamesMap?.[restApiName] || undefined
);

export const getMasterRequestsPagination$ = createSelector(
    getMastersRequestsPaginationMap$,
    getSelectedRestApiName$,
    (requestsPaginationMap, restApiName) => requestsPaginationMap?.[restApiName] || undefined
);

export const getMasterRequests$ = createSelector(getMasterRequestsMap$, getMasterRequestsPagination$, (requests, pagination) => {
    const start = pagination?.pageIndex * pagination?.pageSize;
    const end = (pagination?.pageIndex + 1) * pagination?.pageSize;
    const selectedRequests = requests?.filter((_request, index) => index >= start && index < end) || [];
    return {
        requests: selectedRequests,
        pagingState: pagination?.pageState?.[pagination?.pageIndex] || '',
    };
});

export const getMastersOutboundMappings$ = createSelector(
    getMastersOutboundMappingsMap$,
    getSelectedTableId$,
    (outboundMappingsMap, tableId) => outboundMappingsMap?.[tableId] || undefined
);

export const getReportDetails$ = createSelector(
    getMastersReportDetailsMap$,
    getSelectedTableId$,
    (reportDetailsMap, tableId) => reportDetailsMap?.[tableId] || undefined
);

export const getMasterUploadFiles$ = createSelector(
    getMastersUploadedFilesMap$,
    getSelectedTableId$,
    (uploadedFilesMap, tableId) => uploadedFilesMap?.[tableId] || undefined
);

export const getMasterReportNotifications$ = createSelector(
    getNotificationsMap$,
    getSelectedTableId$,
    (notificationsMap, tableId) => notificationsMap?.[tableId] || undefined
);
